import React, { useCallback, useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { Button } from '@bringg/react-components';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import useOrderExplorerTranslations from '../../use-order-explorer-translations';
import OrderExplorerGrouping from '../order-explorer-grouping/order-explorer-grouping';
import MonitoringFilterBar from '../../../shared/components/monitoring-filter-bar/monitoring-filter-bar';
import FilterButton from '../../../shared/components/filter-button/filter-button';
import useOrderExplorerStore from '../../stores/use-order-explorer-store';
import { View } from '../../../shared/types';
import {
	FilterBarListItemKeys,
	FiltersObject,
	supportedTaskFiltersList
} from '../../../shared/components/monitoring-filter-bar/types';
import { monitoringFiltersProvider } from '../../../shared/components/monitoring-filter-bar/utils/monitoring-filter-provider';
import { userFiltersProvider } from '../../../shared/components/monitoring-filter-bar/utils/user-filters-provider';
import TeamSelector from '../../../shared/components/team-selector/team-selector';
import useFiltersManager from '../../../shared/components/monitoring-filter-bar/hooks/use-filters-manager';
import { TableDefinition } from '../../../shared/stores/groups/table-definition';

import styles from './top-section.module.scss';

const mainFiltersKeys = [FilterBarListItemKeys.TEAMS];

const getTeamErrorStatus = (currentTableDefinition: TableDefinition) =>
	currentTableDefinition ? !currentTableDefinition?.filters.task[FilterBarListItemKeys.TEAMS]?.length : false;

const TopSection = () => {
	const history = useHistory();
	const { orderExplorerStore } = useOrderExplorerStore();
	const { goToRoutesMonitoringView } = useOrderExplorerTranslations();
	const [showFilters, setShowFilters] = useState<boolean>(false);
	const [filtersCount, setFiltersCount] = useState<number>(null);

	const {
		filterBarFiltersWithValues,
		filterObject,
		initialValues,
		initialFilterBarMappedValues,
		initialAdditional,
		setFilterObject
	} = useFiltersManager(userFiltersProvider.getStorageKey(View.tasks), supportedTaskFiltersList);

	const goToRoutesMonitoring = useCallback(() => {
		history.push({
			pathname: '/runs-monitoring'
		});
	}, [history]);

	const onFilterChange = (filters: FiltersObject) => {
		setFiltersCount(monitoringFiltersProvider.count({ filtersObj: filters, keysToIgnore: mainFiltersKeys }));

		orderExplorerStore.tableDefinitionChange({
			filters: {
				...filters,
				...monitoringFiltersProvider.getNormalizedDateRangeFilters(filters),
				...monitoringFiltersProvider.getNormalizeTeamFilter(filters)
			}
		});
		setFilterObject(filters);
	};

	useEffect(() => {
		onFilterChange(initialValues);
	}, [initialValues]);

	return (
		<div className={styles.topSection}>
			<div className={styles.upperSection}>
				<div className={styles.leftContainer}>
					<Button onClick={goToRoutesMonitoring} type="link">
						{goToRoutesMonitoringView}
					</Button>
					<TeamSelector
						selectedTeams={filterObject[FilterBarListItemKeys.TEAMS]}
						onTeamsSelect={teamIds => {
							onFilterChange({ ...filterObject, selectedTeams: teamIds });
						}}
						errorStatus={getTeamErrorStatus(orderExplorerStore.currentTableDefinition)}
					/>
				</div>
				<FilterButton
					onClick={() => {
						setShowFilters(!showFilters);
					}}
					filterObjectCount={filtersCount}
				/>
				<OrderExplorerGrouping />
			</div>
			<div className={classNames(styles.filterBar, { [styles.hideFilterBar]: !showFilters })}>
				<MonitoringFilterBar
					onChange={updatedFilters => {
						const mainFilters: FiltersObject = mainFiltersKeys.reduce((acc, key) => {
							if (filterObject[key]) {
								acc[key] = filterObject[key];
							}

							return acc;
						}, {});

						onFilterChange({ ...updatedFilters, ...mainFilters });
					}}
					filterBarFilters={filterBarFiltersWithValues}
					initialMappedValues={initialFilterBarMappedValues}
					initialAdditional={initialAdditional}
				/>
			</div>
		</div>
	);
};

export default observer(TopSection);
